import React, { useState } from 'react';
import { Modal, Button, Nav, Form, Table, Spinner, Badge, Tab, Tabs, Alert } from 'react-bootstrap';
import { useDialog } from '../../hooks/useDialog';
import { FiTool } from 'react-icons/fi';
import api from '../../configs/api.config';

interface InvoiceComparisonResult {
  freshbooksId: number;
  clientName: string;
  localAmount: number;
  freshBooksAmount: number;
  amountDiscrepancy: number;
  freshBooksStatus: string;
  freshBooksLineCount: number;
  freshBooksFound: boolean;
  freshBooksError: string;
  ticketCount: number;
  dismissedTicketCount: number;
  summonsNumbers: string[];
  hasSuccessfulCharge: boolean;
  zeroedOutAt: string | null;
  isSuspectBug: boolean;
}

interface TriageResponse {
  summary: {
    totalScanned: number;
    bugAffectedCount: number;
    totalAffectedTickets: number;
    affectedClients: number;
  };
  bugAffected: InvoiceComparisonResult[];
  notAffected: InvoiceComparisonResult[];
}

interface BulkResponse {
  summary: {
    totalRequested: number;
    foundInFreshBooks: number;
    suspectBugAffected: number;
    totalAffectedTickets: number;
  };
  results: InvoiceComparisonResult[];
}

interface SingleInvoiceResponse {
  freshbooksId: number;
  invoiceNumber: string;
  status: string;
  amount: string;
  customerId: number;
  createDate: string;
  lineCount: number;
  lines: Array<{
    description: string;
    amount: number;
    unitCost: number;
    qty: number;
  }>;
}

interface TicketDetail {
  summonsNumber: string;
  plate?: string;
  fineAmount?: number;
  penaltyAmount?: number;
  lineAmount?: number;
  violation?: string;
  issueDate?: string;
  status?: string;
  error?: string;
}

interface RepairResponse {
  dryRun?: boolean;
  success?: boolean;
  message?: string;
  freshbooksId: number;
  clientName: string;
  currentFreshBooksAmount?: number;
  previousFreshBooksAmount?: number;
  newFreshBooksAmount?: number;
  expectedInvoiceTotal?: number;
  expectedAmount?: number;
  currentLineCount?: number;
  previousLineCount?: number;
  newLineCount?: number;
  dismissedTicketCount: number;
  totalDismissedAmount?: number;
  ticketDetails: TicketDetail[];
  proposedLines?: Array<{
    description: string;
    amount: number;
    qty: number;
  }>;
}

export default function DevPanel() {
  const { isOpen, handleClose, handleOpen } = useDialog();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('triage');

  // Single invoice lookup
  const [singleInvoiceId, setSingleInvoiceId] = useState('');
  const [singleInvoiceResult, setSingleInvoiceResult] = useState<SingleInvoiceResponse | null>(
    null
  );

  // Bulk lookup
  const [bulkIds, setBulkIds] = useState('');
  const [bulkResults, setBulkResults] = useState<BulkResponse | null>(null);

  // Auto triage
  const [triageLimit, setTriageLimit] = useState('50');
  const [triageResults, setTriageResults] = useState<TriageResponse | null>(null);

  // Repair
  const [repairId, setRepairId] = useState('');
  const [repairResult, setRepairResult] = useState<RepairResponse | null>(null);

  const [error, setError] = useState<string | null>(null);

  const handleSingleLookup = async () => {
    if (!singleInvoiceId.trim()) return;
    setLoading(true);
    setError(null);
    setSingleInvoiceResult(null);
    try {
      const { data } = await api.get(`/DevPanel/freshbooks/invoice/${singleInvoiceId.trim()}`);
      setSingleInvoiceResult(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkLookup = async () => {
    if (!bulkIds.trim()) return;
    setLoading(true);
    setError(null);
    setBulkResults(null);
    try {
      const ids = bulkIds
        .split(/[\n,]+/)
        .map((id) => parseInt(id.trim(), 10))
        .filter((id) => !isNaN(id));
      const { data } = await api.post('/DevPanel/freshbooks/invoices/bulk', {
        freshbooksIds: ids
      });
      setBulkResults(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to fetch invoices');
    } finally {
      setLoading(false);
    }
  };

  const handleAutoTriage = async () => {
    setLoading(true);
    setError(null);
    setTriageResults(null);
    try {
      const { data } = await api.get(`/DevPanel/triage/empty-invoices?limit=${triageLimit}`);
      setTriageResults(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to run triage');
    } finally {
      setLoading(false);
    }
  };

  const handleRepairPreview = async () => {
    if (!repairId.trim()) return;
    setLoading(true);
    setError(null);
    setRepairResult(null);
    try {
      const { data } = await api.post(`/DevPanel/repair/invoice/${repairId.trim()}?dryRun=true`);
      setRepairResult(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to preview repair');
    } finally {
      setLoading(false);
    }
  };

  const handleRepairApply = async () => {
    if (!repairId.trim()) return;
    if (
      !window.confirm(
        `Are you sure you want to repair invoice ${repairId}? This will update FreshBooks.`
      )
    ) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post(`/DevPanel/repair/invoice/${repairId.trim()}?dryRun=false`);
      setRepairResult(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to apply repair');
    } finally {
      setLoading(false);
    }
  };

  const handleRepairFromTriage = async (freshbooksId: number, dryRun: boolean) => {
    if (!dryRun && !window.confirm(`Are you sure you want to repair invoice ${freshbooksId}?`)) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post(`/DevPanel/repair/invoice/${freshbooksId}?dryRun=${dryRun}`);
      setRepairResult(data);
      setActiveTab('repair');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to repair invoice');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  const renderResultsTable = (
    results: InvoiceComparisonResult[],
    showBugColumn = true,
    showRepairButton = false
  ) => (
    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
      <Table striped bordered hover size="sm" className="mt-3">
        <thead style={{ position: 'sticky', top: 0, background: 'white' }}>
          <tr>
            {showBugColumn && <th>Bug?</th>}
            <th>FB ID</th>
            <th>Client</th>
            <th>FB $</th>
            <th>FB Lines</th>
            <th>Dismissed</th>
            {showRepairButton && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {results.map((r) => (
            <tr key={r.freshbooksId} className={r.isSuspectBug ? 'table-danger' : ''}>
              {showBugColumn && (
                <td>
                  {r.isSuspectBug ? <Badge bg="danger">YES</Badge> : <Badge bg="success">No</Badge>}
                </td>
              )}
              <td>{r.freshbooksId}</td>
              <td>{r.clientName}</td>
              <td>{r.freshBooksFound ? formatCurrency(r.freshBooksAmount) : r.freshBooksError}</td>
              <td>{r.freshBooksLineCount}</td>
              <td>{r.dismissedTicketCount}</td>
              {showRepairButton && (
                <td>
                  <Button
                    size="sm"
                    variant="warning"
                    onClick={() => handleRepairFromTriage(r.freshbooksId, true)}
                    disabled={loading}>
                    Preview
                  </Button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );

  return (
    <>
      <Nav.Link className="text-white" onClick={handleOpen}>
        <FiTool className="mx-1" />
        Dev
      </Nav.Link>
      <Modal show={isOpen} onHide={handleClose} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Developer Panel - FreshBooks Inspector</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Tabs
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k || 'triage')}
            className="mb-3">
            <Tab eventKey="triage" title="Auto Triage">
              <p className="text-muted">
                Automatically find invoices where local DB has amount but FreshBooks shows $0
              </p>
              <Form.Group className="mb-3">
                <Form.Label>Limit (max invoices to scan)</Form.Label>
                <Form.Control
                  type="number"
                  value={triageLimit}
                  onChange={(e) => setTriageLimit(e.target.value)}
                  style={{ width: '100px' }}
                />
              </Form.Group>
              <Button onClick={handleAutoTriage} disabled={loading}>
                {loading ? <Spinner size="sm" animation="border" /> : 'Run Auto Triage'}
              </Button>

              {triageResults && (
                <div className="mt-4">
                  <Alert
                    variant={triageResults.summary.bugAffectedCount > 0 ? 'danger' : 'success'}>
                    <strong>Summary:</strong> Scanned {triageResults.summary.totalScanned} invoices.
                    Found <strong>{triageResults.summary.bugAffectedCount}</strong> affected by bug
                    ({triageResults.summary.totalAffectedTickets} dismissed tickets) across{' '}
                    {triageResults.summary.affectedClients} clients.
                  </Alert>

                  {triageResults.bugAffected.length > 0 && (
                    <>
                      <h6>Bug Affected Invoices:</h6>
                      {renderResultsTable(triageResults.bugAffected, false, true)}
                    </>
                  )}
                </div>
              )}
            </Tab>

            <Tab eventKey="bulk" title="Bulk Compare">
              <p className="text-muted">
                Paste FreshBooks invoice IDs (one per line or comma-separated) to compare with local
                DB
              </p>
              <Form.Group className="mb-3">
                <Form.Label>FreshBooks Invoice IDs</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  placeholder="231401&#10;231402&#10;231403"
                  value={bulkIds}
                  onChange={(e) => setBulkIds(e.target.value)}
                />
              </Form.Group>
              <Button onClick={handleBulkLookup} disabled={loading || !bulkIds.trim()}>
                {loading ? <Spinner size="sm" animation="border" /> : 'Compare Invoices'}
              </Button>

              {bulkResults && (
                <div className="mt-4">
                  <Alert
                    variant={bulkResults.summary.suspectBugAffected > 0 ? 'warning' : 'success'}>
                    <strong>Summary:</strong> Checked {bulkResults.summary.totalRequested} invoices.
                    Found in FreshBooks: {bulkResults.summary.foundInFreshBooks}. Suspect bug
                    affected: <strong>{bulkResults.summary.suspectBugAffected}</strong>
                    {bulkResults.summary.suspectBugAffected > 0 && (
                      <> ({bulkResults.summary.totalAffectedTickets} dismissed tickets)</>
                    )}
                  </Alert>
                  {renderResultsTable(bulkResults.results)}
                </div>
              )}
            </Tab>

            <Tab eventKey="single" title="Single Lookup">
              <p className="text-muted">Look up a single FreshBooks invoice to see its details</p>
              <Form.Group className="mb-3">
                <Form.Label>FreshBooks Invoice ID</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    placeholder="231401"
                    value={singleInvoiceId}
                    onChange={(e) => setSingleInvoiceId(e.target.value)}
                    style={{ width: '200px' }}
                  />
                  <Button
                    onClick={handleSingleLookup}
                    disabled={loading || !singleInvoiceId.trim()}>
                    {loading ? <Spinner size="sm" animation="border" /> : 'Lookup'}
                  </Button>
                </div>
              </Form.Group>

              {singleInvoiceResult && (
                <div className="mt-4">
                  <Alert variant="info">
                    <strong>Invoice #{singleInvoiceResult.invoiceNumber}</strong>
                    <br />
                    Status: {singleInvoiceResult.status} | Amount: ${singleInvoiceResult.amount} |
                    Lines: {singleInvoiceResult.lineCount}
                  </Alert>
                  {singleInvoiceResult.lines && singleInvoiceResult.lines.length > 0 && (
                    <Table striped bordered size="sm">
                      <thead>
                        <tr>
                          <th>Description</th>
                          <th>Qty</th>
                          <th>Unit Cost</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {singleInvoiceResult.lines.map((line, idx) => (
                          <tr key={idx}>
                            <td>{line.description}</td>
                            <td>{line.qty}</td>
                            <td>{formatCurrency(line.unitCost)}</td>
                            <td>{formatCurrency(line.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </div>
              )}
            </Tab>

            <Tab eventKey="repair" title="Repair Invoice">
              <p className="text-muted">
                Repair a blank FreshBooks invoice by regenerating lines from ticket data
              </p>
              <Form.Group className="mb-3">
                <Form.Label>FreshBooks Invoice ID</Form.Label>
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    placeholder="6760839"
                    value={repairId}
                    onChange={(e) => setRepairId(e.target.value)}
                    style={{ width: '200px' }}
                  />
                  <Button
                    variant="info"
                    onClick={handleRepairPreview}
                    disabled={loading || !repairId.trim()}>
                    {loading ? <Spinner size="sm" animation="border" /> : 'Preview Repair'}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleRepairApply}
                    disabled={loading || !repairId.trim() || !repairResult?.dryRun}>
                    Apply Repair
                  </Button>
                </div>
              </Form.Group>

              {repairResult && (
                <div className="mt-4">
                  <Alert variant={repairResult.dryRun ? 'warning' : 'success'}>
                    {repairResult.dryRun ? (
                      <>
                        <strong>DRY RUN - No changes made</strong>
                        <br />
                        {repairResult.message}
                      </>
                    ) : (
                      <>
                        <strong>Repair Applied Successfully!</strong>
                        <br />
                        Previous Amount:{' '}
                        {formatCurrency(repairResult.previousFreshBooksAmount || 0)} → New Amount:{' '}
                        {formatCurrency(repairResult.newFreshBooksAmount || 0)}
                      </>
                    )}
                  </Alert>

                  <div className="row">
                    <div className="col-md-6">
                      <h6>Invoice Details</h6>
                      <Table size="sm" bordered>
                        <tbody>
                          <tr>
                            <td>FreshBooks ID</td>
                            <td>{repairResult.freshbooksId}</td>
                          </tr>
                          <tr>
                            <td>Client</td>
                            <td>{repairResult.clientName}</td>
                          </tr>
                          <tr>
                            <td>Current FB Amount</td>
                            <td>
                              {formatCurrency(
                                repairResult.currentFreshBooksAmount ||
                                  repairResult.previousFreshBooksAmount ||
                                  0
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td>Expected Amount</td>
                            <td>
                              {formatCurrency(
                                repairResult.expectedInvoiceTotal ||
                                  repairResult.expectedAmount ||
                                  0
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td>Total Dismissed Amount</td>
                            <td>{formatCurrency(repairResult.totalDismissedAmount || 0)}</td>
                          </tr>
                          <tr>
                            <td>Dismissed Tickets</td>
                            <td>{repairResult.dismissedTicketCount}</td>
                          </tr>
                        </tbody>
                      </Table>
                    </div>

                    <div className="col-md-6">
                      <h6>Ticket Details</h6>
                      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        <Table size="sm" bordered>
                          <thead>
                            <tr>
                              <th>Summons #</th>
                              <th>Plate</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {repairResult.ticketDetails.map((t, idx) => (
                              <tr key={idx} className={t.error ? 'table-danger' : ''}>
                                <td>{t.summonsNumber}</td>
                                <td>{t.plate || t.error || '-'}</td>
                                <td>{t.lineAmount ? formatCurrency(t.lineAmount) : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </div>
                  </div>

                  {repairResult.proposedLines && repairResult.proposedLines.length > 0 && (
                    <>
                      <h6 className="mt-3">Proposed Invoice Lines</h6>
                      <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        <Table size="sm" bordered>
                          <thead>
                            <tr>
                              <th>Description</th>
                              <th>Qty</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {repairResult.proposedLines.map((line, idx) => (
                              <tr key={idx}>
                                <td style={{ whiteSpace: 'pre-wrap', fontSize: '0.85em' }}>
                                  {line.description}
                                </td>
                                <td>{line.qty}</td>
                                <td>{formatCurrency(line.amount || 0)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </>
                  )}
                </div>
              )}
            </Tab>
          </Tabs>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
